import { arcadeApiClient } from '../apiClients'
import useSWR from 'swr'
import { PlayerMeta } from 'types'
import { useCallback, useEffect, useState } from 'react'

const playerFetcher = async (
  query: string,
): Promise<{ people: PlayerMeta[] }> => {
  return arcadeApiClient.get(`/multi_platform/${query}`).then(res => res.data)
}

const defaultEndpoint = 'manage/people'

const usePlayers = ({
  query,
  teamId,
  limit = 50,
  endpoint,
  includeSelf = false
}: {
  query?: string
  teamId?: number
  limit: number
  endpoint: string | null
  includeSelf?: boolean
}) => {
  let [page, setPage] = useState(0)
  const [moreLeft, setMoreLeft] = useState(true)
  const [players, setPlayers] = useState<PlayerMeta[]>([])

  // If the params change, start again from page 0
  useEffect(() => {
    setPage(0)
    // This is a hack to prevent a wacky request when the team
    // or limit changes, causeing a flickering render or a failure of 'Add All'
    page = 0
    setMoreLeft(true)
  }, [query, teamId, limit])

  const {
    data,
    isValidating: loading,
    error,
  } = useSWR(
    `${endpoint || defaultEndpoint}?simple=true&sort_by=name&sort_direction=asc&page=${page}&rows_per_page=${limit}${
      query ? `&query=${query}` : ''
    }${teamId ? `&team_ids[]=${teamId}` : ''}${includeSelf ? '&include_self=true' : ''}`,
    playerFetcher,
  )

  // When we load a page, push that page onto the full player list
  // If it's not a complete page, assume there are no more players to fetch
  // If this is page 0, start the list from scratch.
  useEffect(() => {
    if (!data) return
    if (page > 0 && data.people.length < limit) {
      setMoreLeft(false)
    }
    if (page === 0) {
      setPlayers(data.people)
      return
    }
    const playerIds = players.map(p => p.id)
    const newPeople = data.people.filter(p => !playerIds.includes(p.id))
    setPlayers([...players, ...newPeople])
  }, [data])

  // Load more by loading the next page.
  const more = useCallback(() => {
    if (loading || !moreLeft) return
    setPage(page + 1)
  }, [setPage, loading, moreLeft])

  return {
    players,
    loading,
    more,
    error,
  }
}
export default usePlayers
